import { useEffect, useState } from "react";
import { Download, Share, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export const InstallAppButton = ({ compact = false }) => {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") setPrompt(null);
      return;
    }
    setGuide(true);
  };

  return (
    <>
      <Button onClick={install} variant="outline" className={`${compact ? "w-full justify-start" : ""} rounded-lg border-[#5aa469]/40 bg-[#5aa469]/5 text-[#3f7a4a] hover:bg-[#5aa469]/10`} data-testid="install-app-btn">
        <Smartphone className="h-4 w-4 mr-2" /> Scarica l'app
      </Button>
      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent className="sm:max-w-md" data-testid="install-guide-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Installa MenuOne sul telefono</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Avrai l'icona MenuOne nella schermata Home, come una vera app, senza passare dal browser.</p>
          {isIOS() ? (
            <ol className="space-y-3 text-sm text-gray-700 mt-2">
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">1</span><span>Apri questa pagina in <strong>Safari</strong> e tocca il pulsante <strong>Condividi</strong> <Share className="inline h-4 w-4 -mt-0.5" /> in basso.</span></li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">2</span><span>Scorri e scegli <strong>"Aggiungi alla schermata Home"</strong>.</span></li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">3</span><span>Tocca <strong>Aggiungi</strong>: trovi MenuOne tra le tue app.</span></li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm text-gray-700 mt-2">
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">1</span><span>Apri il menu del browser (i <strong>tre puntini</strong> in alto a destra su Chrome).</span></li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">2</span><span>Scegli <strong>"Installa app"</strong> oppure <strong>"Aggiungi a schermata Home"</strong>.</span></li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469] text-white text-xs flex items-center justify-center shrink-0">3</span><span>Conferma: l'icona MenuOne compare tra le tue app.</span></li>
            </ol>
          )}
          <Button onClick={() => setGuide(false)} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white mt-2" data-testid="install-guide-close"><X className="h-4 w-4 mr-1" /> Ho capito</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const InstallAppCard = () => {
  const [visible, setVisible] = useState(!isStandalone() && !localStorage.getItem("menuone_install_dismissed"));
  if (!visible) return null;
  return (
    <div className="rounded-2xl border border-[#5aa469]/30 bg-[#5aa469]/5 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="install-app-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#5aa469] text-white flex items-center justify-center"><Download className="h-5 w-5" /></div>
        <div>
          <p className="font-semibold">Scarica l'app MenuOne</p>
          <p className="text-gray-500 text-sm">Icona sulla Home del telefono, si apre a schermo intero senza browser. Gratis.</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <InstallAppButton />
        <button onClick={() => { localStorage.setItem("menuone_install_dismissed", "1"); setVisible(false); }} className="text-xs text-gray-400 hover:text-gray-600" data-testid="install-app-dismiss">Non ora</button>
      </div>
    </div>
  );
};
